import { useEffect, useMemo, useState } from 'react'
import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaCompass,
  FaEye,
  FaHeart,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaShieldAlt,
  FaSignOutAlt,
  FaStar,
  FaTrash,
  FaUser,
  FaUsers
} from 'react-icons/fa'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import ConfirmModal from 'components/confirmModal'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'
import { getItineraryPublicPath } from 'utils/itineraryUrl'

import * as S from '../style'

type ModerationStatus = 'visible' | 'hidden'

type AdminItinerary = {
  id: string
  title: string
  made: boolean
  tripDate?: string | null
  coverImage?: string
  days: number
  spent: number
  createdAt: string
  updatedAt: string
  authorId: string
  authorFirstname: string
  authorLastname: string
  authorEmail: string
  authorProfilePicture?: string
  moderationStatus: ModerationStatus
  moderationReason?: string | null
  moderationUpdatedAt?: string | null
  cityCount: number
  ratings: number
  averageRating: number
  favorites: number
  views: number
  inspirations: number
}

type ItinerariesResponse = {
  items: AdminItinerary[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  summary: {
    total: number
    published: number
    planned: number
    hidden: number
  }
}

type ItineraryDetails = AdminItinerary & {
  summary?: string | null
  type?: string
  inspiredFromItineraryId?: string | null
  cities: Array<{
    id: string
    name: string
    description?: string
    latitude?: number
    longitude?: number
    order?: number
    dayNumber?: number
  }>
  interests: Array<{
    id: string
    name: string
  }>
  collaborators: Array<{
    id: string
    status: string
    firstname: string
    lastname: string
    email: string
  }>
  recentViews: Array<{
    user_id?: string | null
    session_id?: string | null
    created_at: string
  }>
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value ?? 0))

const AdminItineraries = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [tripStatus, setTripStatus] = useState('all')
  const [moderationStatus, setModerationStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [nextStatus, setNextStatus] = useState<ModerationStatus>('visible')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 450)

    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [tripStatus, moderationStatus])

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '20',
      tripStatus,
      moderationStatus
    })

    if (debouncedSearch) params.set('search', debouncedSearch)
    return params.toString()
  }, [debouncedSearch, moderationStatus, page, tripStatus])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-itineraries', queryString],
    queryFn: async () => {
      const response = await api.get<ItinerariesResponse>(
        `/admin/itineraries?${queryString}`
      )
      return response.data
    },
    staleTime: 30 * 1000
  })

  const { data: selected, isLoading: loadingDetails } = useQuery({
    queryKey: ['admin-itinerary-details', selectedId],
    queryFn: async () => {
      const response = await api.get<ItineraryDetails>(
        `/admin/itineraries/${selectedId}`
      )
      return response.data
    },
    enabled: Boolean(selectedId),
    staleTime: 15 * 1000
  })

  useEffect(() => {
    if (!selected) return
    setNextStatus(selected.moderationStatus)
    setReason(selected.moderationReason || '')
  }, [selected])

  const closeDetails = () => {
    if (saving || deleting) return
    setSelectedId(null)
    setDeleteModalOpen(false)
  }

  const saveModeration = async () => {
    if (!selectedId || saving) return

    setSaving(true)

    try {
      await api.patch(`/admin/itineraries/${selectedId}/state`, {
        status: nextStatus,
        reason
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-itineraries'] }),
        queryClient.invalidateQueries({
          queryKey: ['admin-itinerary-details', selectedId]
        }),
        queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['AllItineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['Itineraries'] })
      ])

      toast.success(
        nextStatus === 'hidden'
          ? 'Roteiro ocultado da área pública.'
          : 'Roteiro reativado na área pública.',
        { theme: 'dark' }
      )
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível atualizar a moderação do roteiro.',
        { theme: 'dark' }
      )
    } finally {
      setSaving(false)
    }
  }

  const deleteItinerary = async () => {
    if (!selectedId || deleting) return

    setDeleting(true)

    try {
      await api.delete(`/admin/itineraries/${selectedId}`)

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-itineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['AllItineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['Itineraries'] })
      ])

      toast.success('Roteiro excluído pela moderação.', { theme: 'dark' })
      setDeleteModalOpen(false)
      setSelectedId(null)
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível excluir este roteiro.',
        { theme: 'dark' }
      )
    } finally {
      setDeleting(false)
    }
  }

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt />
            Admin
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            <S.SidebarNavLink to="/admin">
              <FaChartLine />
              Dashboard
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/usuarios">
              <FaUsers />
              Usuários
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/localidades">
              <FaMapMarkerAlt />
              Localidades
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/roteiros" $active>
              <FaRoute />
              Roteiros
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/anuncios">
              <FaBullhorn />
              Anúncios
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/radar">
              <FaSatelliteDish />
              Radar
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/comissoes">
              <FaDollarSign />
              Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser />
            Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft />
            Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Roteiros</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={fullName || 'Administrador'}
                />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || 'Administrador'}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>Admin</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <S.DashboardHeader>
            <div>
              <h1>Moderação de roteiros</h1>
              <p>
                Consulte conteúdo publicado, analise sinais de engajamento e
                retire temporariamente do ar o que precisar de revisão.
              </p>
            </div>
          </S.DashboardHeader>

          {data && (
            <S.UserSummaryGrid>
              <S.UserSummaryCard>
                <FaRoute />
                <div>
                  <strong>{data.summary.total}</strong>
                  <span>Total de roteiros</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="green">
                <FaEye />
                <div>
                  <strong>{data.summary.published}</strong>
                  <span>Publicados</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="blue">
                <FaMapMarkerAlt />
                <div>
                  <strong>{data.summary.planned}</strong>
                  <span>Planejados</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="red">
                <FaShieldAlt />
                <div>
                  <strong>{data.summary.hidden}</strong>
                  <span>Ocultos pela moderação</span>
                </div>
              </S.UserSummaryCard>
            </S.UserSummaryGrid>
          )}

          <S.UserToolbar>
            <S.UserSearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por título, autor, e-mail ou cidade..."
            />
            <S.UserFilter
              value={tripStatus}
              onChange={(event) => setTripStatus(event.target.value)}
            >
              <option value="all">Todos os roteiros</option>
              <option value="published">Publicados</option>
              <option value="planned">Planejados</option>
            </S.UserFilter>
            <S.UserFilter
              value={moderationStatus}
              onChange={(event) => setModerationStatus(event.target.value)}
            >
              <option value="all">Todos na moderação</option>
              <option value="visible">Visíveis</option>
              <option value="hidden">Ocultos</option>
            </S.UserFilter>
          </S.UserToolbar>

          {isLoading && <S.StateCard>Carregando roteiros...</S.StateCard>}

          {isError && (
            <S.StateCard $error>
              Não foi possível carregar os roteiros.
            </S.StateCard>
          )}

          {data && (
            <S.UserTableCard>
              <S.ItineraryAdminTable>
                <thead>
                  <tr>
                    <th>Roteiro</th>
                    <th>Autor</th>
                    <th>Viagem</th>
                    <th>Moderação</th>
                    <th>Engajamento</th>
                    <th>Criado em</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <S.ItineraryIdentity>
                          <S.ItineraryThumb>
                            <img
                              src={
                                item.coverImage ||
                                '/images/default_itinerary_cover.svg'
                              }
                              alt=""
                            />
                          </S.ItineraryThumb>
                          <div>
                            <strong>{item.title}</strong>
                            <span>
                              {item.days} {item.days === 1 ? 'dia' : 'dias'} ·{' '}
                              {item.cityCount}{' '}
                              {item.cityCount === 1 ? 'cidade' : 'cidades'}
                            </span>
                          </div>
                        </S.ItineraryIdentity>
                      </td>
                      <td>
                        <S.ItineraryAuthor>
                          <strong>
                            {item.authorFirstname} {item.authorLastname}
                          </strong>
                          <span>{item.authorEmail}</span>
                        </S.ItineraryAuthor>
                      </td>
                      <td>
                        <S.TripChip $made={item.made}>
                          {item.made ? 'Realizada' : 'Planejada'}
                        </S.TripChip>
                      </td>
                      <td>
                        <S.ModerationChip $status={item.moderationStatus}>
                          {item.moderationStatus === 'hidden'
                            ? 'Oculto'
                            : 'Visível'}
                        </S.ModerationChip>
                      </td>
                      <td>
                        <S.EngagementInline>
                          <span title="Visualizações">
                            <FaEye /> {item.views}
                          </span>
                          <span title="Favoritos">
                            <FaHeart /> {item.favorites}
                          </span>
                          <span title="Nota média">
                            <FaStar /> {item.averageRating.toFixed(1)}
                          </span>
                        </S.EngagementInline>
                      </td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <S.TableAction
                          type="button"
                          onClick={() => setSelectedId(item.id)}
                        >
                          Moderar
                        </S.TableAction>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </S.ItineraryAdminTable>

              {data.items.length === 0 && (
                <S.EmptyText>
                  Nenhum roteiro encontrado com os filtros selecionados.
                </S.EmptyText>
              )}

              <S.Pagination>
                <span>
                  {data.pagination.total} roteiros · página{' '}
                  {data.pagination.page} de {data.pagination.pages}
                </span>
                <div>
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={page >= data.pagination.pages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </S.Pagination>
            </S.UserTableCard>
          )}
        </S.Content>
      </S.Main>

      {selectedId && (
        <S.AdminModalOverlay onMouseDown={closeDetails}>
          <S.AdminModal
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <S.AdminModalHeader>
              <div>
                <S.TopbarEyebrow>Moderação de conteúdo</S.TopbarEyebrow>
                <h2>Detalhes do roteiro</h2>
              </div>
              <button type="button" onClick={closeDetails}>
                ×
              </button>
            </S.AdminModalHeader>

            {loadingDetails || !selected ? (
              <S.StateCard>Carregando roteiro...</S.StateCard>
            ) : (
              <>
                <S.ItineraryDetailHero>
                  <S.ItineraryDetailCover>
                    <img
                      src={
                        selected.coverImage ||
                        '/images/default_itinerary_cover.svg'
                      }
                      alt=""
                    />
                  </S.ItineraryDetailCover>
                  <div>
                    <S.TripChip $made={selected.made}>
                      {selected.made ? 'Viagem realizada' : 'Planejada'}
                    </S.TripChip>
                    <h3>{selected.title}</h3>
                    <p>
                      por{' '}
                      <a
                        href={`/usuario/${selected.authorId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {selected.authorFirstname} {selected.authorLastname}
                      </a>
                    </p>
                    <span>
                      {selected.days} dias · {formatCurrency(selected.spent)}
                    </span>
                  </div>
                </S.ItineraryDetailHero>

                <S.AdminUserStats>
                  <div>
                    <strong>{selected.views}</strong>
                    <span>Visualizações</span>
                  </div>
                  <div>
                    <strong>{selected.favorites}</strong>
                    <span>Favoritos</span>
                  </div>
                  <div>
                    <strong>{selected.averageRating.toFixed(1)}</strong>
                    <span>Nota média ({selected.ratings})</span>
                  </div>
                  <div>
                    <strong>{selected.inspirations}</strong>
                    <span>Inspirações geradas</span>
                  </div>
                </S.AdminUserStats>

                {selected.summary && (
                  <S.ItinerarySummaryBox>
                    <strong>Resumo</strong>
                    <p>{selected.summary}</p>
                  </S.ItinerarySummaryBox>
                )}

                <S.ItineraryDetailGrid>
                  <div>
                    <h3>Cidades</h3>
                    <S.TagList>
                      {selected.cities.map((city) => (
                        <span key={city.id}>{city.name}</span>
                      ))}
                    </S.TagList>
                  </div>
                  <div>
                    <h3>Interesses</h3>
                    <S.TagList>
                      {selected.interests.length ? (
                        selected.interests.map((interest) => (
                          <span key={interest.id}>{interest.name}</span>
                        ))
                      ) : (
                        <em>Nenhum interesse cadastrado.</em>
                      )}
                    </S.TagList>
                  </div>
                </S.ItineraryDetailGrid>

                {selected.collaborators.length > 0 && (
                  <S.ItinerarySummaryBox>
                    <strong>Colaboradores</strong>
                    <S.CollaboratorList>
                      {selected.collaborators.map((collaborator) => (
                        <span key={collaborator.id}>
                          {collaborator.firstname} {collaborator.lastname} ·{' '}
                          {collaborator.status}
                        </span>
                      ))}
                    </S.CollaboratorList>
                  </S.ItinerarySummaryBox>
                )}

                <S.ModerationForm>
                  <label>
                    Visibilidade
                    <select
                      value={nextStatus}
                      onChange={(event) =>
                        setNextStatus(event.target.value as ModerationStatus)
                      }
                    >
                      <option value="visible">Visível</option>
                      <option value="hidden">Oculto pela moderação</option>
                    </select>
                  </label>

                  <label>
                    Motivo / observação
                    <textarea
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Explique o motivo da moderação. Esta anotação é administrativa."
                    />
                  </label>
                </S.ModerationForm>

                {nextStatus === 'hidden' && (
                  <S.AdminNotice>
                    Ao salvar como oculto, o roteiro deixa de aparecer para o
                    público e em favoritos de terceiros. O autor e colaboradores
                    ainda poderão acessá-lo para ajustes.
                  </S.AdminNotice>
                )}

                <S.AdminModalActions>
                  {selected.moderationStatus === 'visible' && (
                    <a
                      href={getItineraryPublicPath({
                        id: selected.id,
                        cities: selected.cities
                      })}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Visualizar no site
                    </a>
                  )}
                  <button
                    type="button"
                    className="danger"
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    <FaTrash />
                    Excluir
                  </button>
                  <button type="button" onClick={closeDetails}>
                    Fechar
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={saving}
                    onClick={saveModeration}
                  >
                    {saving ? 'Salvando...' : 'Salvar moderação'}
                  </button>
                </S.AdminModalActions>
              </>
            )}
          </S.AdminModal>
        </S.AdminModalOverlay>
      )}

      <ConfirmModal
        isOpen={deleteModalOpen}
        title="Excluir roteiro?"
        description={
          <>
            Esta ação removerá administrativamente{' '}
            <strong>“{selected?.title || 'este roteiro'}”</strong>. Use a
            exclusão apenas quando ocultar o conteúdo não for suficiente.
          </>
        }
        confirmText="Excluir roteiro"
        cancelText="Cancelar"
        loading={deleting}
        onConfirm={deleteItinerary}
        onCancel={() => !deleting && setDeleteModalOpen(false)}
      />
    </S.Page>
  )
}

export default AdminItineraries
